import { forwardRef, useId, useState } from 'react';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';

const CONTROL =
  'border-line bg-surface text-ink placeholder:text-ink-muted/70 rounded-input w-full border ' +
  'px-2.5 py-1.5 text-sm shadow-xs transition hover:border-brand-300 ' +
  'focus:border-brand-500 focus:ring-brand-100 focus:ring-2 focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

const INVALID =
  'border-danger-500 hover:border-danger-500 focus:border-danger-500 focus:ring-danger-100';

/** A 32px control for toolbars (list-card search and filters). */
const SMALL = 'h-8 py-1';

/**
 * Label + control + hint/error, so every form field looks and behaves alike.
 *
 * `hideLabel` keeps the label for screen readers but not on screen -- for a
 * compact toolbar filter whose placeholder or first option already says what
 * it is. A placeholder is never a label on its own.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  optional,
  hideLabel = false,
  children,
  className,
}) {
  return (
    <div className={cn(hideLabel ? 'space-y-0' : 'space-y-1', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className={cn('text-ink block text-[12px] font-medium', hideLabel && 'sr-only')}
        >
          {label}
          {required && <span className="ml-0.5 text-danger-600">*</span>}
          {optional && !required && (
            <span className="text-ink-muted ml-1 font-normal">(optional)</span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-caption text-danger-600" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="text-caption text-ink-muted">{hint}</p>
      )}
    </div>
  );
}

export const Input = forwardRef(function Input(
  {
    label,
    error,
    hint,
    className,
    id,
    required,
    optional,
    hideLabel,
    size,
    wrapperClassName,
    type,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  // Every password field can be shown while typing, to catch a typo before
  // it locks someone out.
  const isPassword = type === 'password';
  const [revealed, setRevealed] = useState(false);

  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      hint={hint}
      required={required}
      optional={optional}
      hideLabel={hideLabel}
      className={wrapperClassName}
    >
      {isPassword ? (
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={revealed ? 'text' : 'password'}
            aria-invalid={Boolean(error) || undefined}
            className={cn(CONTROL, 'pr-9', size === 'sm' && SMALL, error && INVALID, className)}
            required={required}
            {...props}
          />
          <button
            type="button"
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            aria-controls={inputId}
            title={revealed ? 'Hide password' : 'Show password'}
            className="text-ink-muted hover:text-brand-700 focus-visible:ring-brand-300 absolute inset-y-0 right-0 grid w-9 place-items-center rounded-r-[var(--radius-input)] focus-visible:ring-2 focus-visible:outline-none"
          >
            <Icon name={revealed ? 'eyeOff' : 'eye'} size={16} />
          </button>
        </div>
      ) : (
        <input
          ref={ref}
          id={inputId}
          type={type}
          aria-invalid={Boolean(error) || undefined}
          className={cn(CONTROL, size === 'sm' && SMALL, error && INVALID, className)}
          required={required}
          {...props}
        />
      )}
    </Field>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, error, hint, className, id, required, optional, rows = 4, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      hint={hint}
      required={required}
      optional={optional}
    >
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        aria-invalid={Boolean(error) || undefined}
        className={cn(CONTROL, error && INVALID, className)}
        required={required}
        {...props}
      />
    </Field>
  );
});

export const Select = forwardRef(function Select(
  {
    label,
    error,
    hint,
    className,
    id,
    required,
    optional,
    hideLabel,
    size,
    wrapperClassName,
    children,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      hint={hint}
      required={required}
      optional={optional}
      hideLabel={hideLabel}
      className={wrapperClassName}
    >
      <select
        ref={ref}
        id={inputId}
        aria-invalid={Boolean(error) || undefined}
        className={cn(CONTROL, size === 'sm' && SMALL, error && INVALID, className)}
        required={required}
        {...props}
      >
        {children}
      </select>
    </Field>
  );
});

export const Checkbox = forwardRef(function Checkbox(
  { label, error, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="space-y-1">
      <label htmlFor={inputId} className="flex items-start gap-2 text-sm">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className={cn(
            'border-line text-brand-600 focus:ring-brand-500 mt-0.5 size-4 rounded border',
            className,
          )}
          aria-invalid={Boolean(error) || undefined}
          {...props}
        />
        <span className="text-ink-muted">{label}</span>
      </label>
      {error && (
        <p className="text-caption text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
